import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTour } from '../contexts/TourContext';
import { Button } from './ui/button';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

const ProductTour = () => {
  const { isTourActive, currentStep, nextStep, prevStep, skipTour, endTour } = useTour();
  const navigate = useNavigate();

  const tourSteps = [
    {
      title: 'Welcome to Baad.tv!',
      content: 'Let\'s take a quick tour to show you around. This will only take a minute!',
      icon: '/sheep-logo.png',
      page: '/dashboard',
    },
    {
      title: 'Your Paddock',
      content: 'This is your home base! See quick stats for all your shows: currently watching, waiting for, completed, or want to watch.',
      icon: 'https://customer-assets.emergentagent.com/job_baadtv-tracker/artifacts/069gpugl_Gate%202.png',
      page: '/dashboard',
    },
    {
      title: 'My BaadTV',
      content: 'Click here to manage your show collection! Add shows, rate them, write reviews, and organise by status.',
      icon: 'https://customer-assets.emergentagent.com/job_flockview/artifacts/8oodua1r_Ribbon.png',
      page: '/my-shows',
    },
    {
      title: 'My Feed',
      content: 'See reviews from friends you follow! Like and comment on reviews, add shows to your watchlist with one click, and share your own opinions with your flock.',
      icon: 'https://customer-assets.emergentagent.com/job_flockview/artifacts/zl5j3mco_Bale%20of%20Hay.png',
      page: '/feed',
    },
    {
      title: 'The Flock',
      content: 'Search for friends by their @username to follow them. Privacy first: only people who know your handle can find you! You can also recommend shows directly to friends here.',
      icon: 'https://customer-assets.emergentagent.com/job_flockview/artifacts/uqlewzen_fLOCK.png',
      page: '/friends',
    },
    {
      title: 'Help Screen',
      content: 'Need help? Click the Help link in the navigation anytime to access guides and FAQs.',
      icon: 'https://customer-assets.emergentagent.com/job_flockview/artifacts/us6v457g_Logo.png',
      page: '/help',
    },
    {
      title: 'You\'re All Set!',
      content: null,
      showTips: true,
      icon: 'https://customer-assets.emergentagent.com/job_flockview/artifacts/v68hcgy7_Party%20Sheep.png',
      page: '/dashboard',
    },
  ];

  const currentTourStep = tourSteps[currentStep];
  const totalSteps = tourSteps.length;
  const progressPercent = ((currentStep + 1) / totalSteps) * 100;

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      const nextStepData = tourSteps[currentStep + 1];
      navigate(nextStepData.page);
      nextStep();
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      const prevStepData = tourSteps[currentStep - 1];
      navigate(prevStepData.page);
      prevStep();
    }
  };

  const handleSkip = () => {
    skipTour();
    navigate('/dashboard');
  };

  const handleComplete = () => {
    endTour();
    navigate('/dashboard');
  };

  if (!isTourActive) return null;

  return (
    <div className="tour-overlay">
      <div className="tour-modal">
        <button className="tour-close" onClick={handleSkip} aria-label="Close tour">
          <X size={28} />
        </button>

        <div className="tour-header">
          {currentTourStep.icon && (
            <img src={currentTourStep.icon} alt={currentTourStep.title} className="tour-icon" />
          )}
          <h2 className="tour-title">{currentTourStep.title}</h2>
        </div>

        {currentTourStep.showTips ? (
          <div className="tour-tips">
            <h3 className="tips-heading">Quick Tips:</h3>
            <ul className="tips-list">
              <li>Rate and review shows to share with your flock</li>
              <li>Check notifications (bell icon) for friend activity</li>
              <li>Recommend shows using the recommend icon</li>
              <li>Check your posts in My Feed for likes and comments</li>
            </ul>
            <p className="tour-restart-note">You can restart this tour anytime from your profile menu!</p>
          </div>
        ) : (
          <p className="tour-content">{currentTourStep.content}</p>
        )}

        <div className="tour-progress-section">
          <div className="tour-step-text">Step {currentStep + 1} of {totalSteps}</div>
          <div className="tour-progress-track">
            <div className="tour-progress-fill" style={{ width: `${progressPercent}%` }} />
          </div>
        </div>

        <div className="tour-actions">
          <Button variant="outline" onClick={handleSkip} className="tour-btn-skip">
            Skip Tour
          </Button>

          {currentStep > 0 && (
            <Button variant="outline" onClick={handlePrevious} className="tour-btn-back">
              <ChevronLeft size={18} />
              Back
            </Button>
          )}
          {currentStep === 0 && <div className="tour-btn-spacer" />}

          <Button onClick={handleNext} className="tour-btn-next">
            {currentStep === tourSteps.length - 1 ? 'Finish' : 'Next'}
            {currentStep < tourSteps.length - 1 && <ChevronRight size={18} />}
          </Button>
        </div>
      </div>

      <style jsx>{`
        .tour-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.88);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 10000;
          padding: 1rem;
        }

        .tour-modal {
          background: #1a1a1a;
          border: 3px solid #E62F88;
          border-radius: 20px;
          padding: 2rem 2.5rem;
          max-width: 620px;
          width: 100%;
          position: relative;
          box-shadow: 0 25px 70px rgba(230, 47, 136, 0.4);
        }

        .tour-close {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          background: transparent;
          border: none;
          color: #999999;
          cursor: pointer;
          padding: 0.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: color 0.2s;
        }

        .tour-close:hover {
          color: #ffffff;
        }

        .tour-header {
          display: flex;
          align-items: center;
          gap: 1.2rem;
          margin-bottom: 2rem;
        }

        .tour-icon {
          width: 60px;
          height: 60px;
          object-fit: contain;
          flex-shrink: 0;
        }

        .tour-title {
          color: #E62F88;
          font-size: 2rem;
          font-weight: 700;
          margin: 0;
          line-height: 1.1;
        }

        .tour-content {
          color: #ffffff;
          font-size: 1.1rem;
          line-height: 1.5;
          margin: 0 0 2rem 0;
          text-align: left;
        }

        .tour-tips {
          margin-bottom: 2rem;
        }

        .tips-heading {
          color: #ffffff;
          font-size: 1.3rem;
          font-weight: 600;
          margin: 0 0 1rem 0;
        }

        .tips-list {
          list-style: none;
          padding: 0;
          margin: 0 0 1.2rem 0;
        }

        .tips-list li {
          color: #b0b0b0;
          font-size: 1rem;
          line-height: 1.6;
          margin-bottom: 0.6rem;
          padding-left: 0;
        }

        .tour-restart-note {
          color: #ffffff;
          font-size: 1rem;
          margin: 0;
          font-weight: 400;
        }

        .tour-progress-section {
          margin-bottom: 2rem;
        }

        .tour-step-text {
          color: #b0b0b0;
          font-size: 0.85rem;
          margin-bottom: 0.6rem;
          font-weight: 400;
        }

        .tour-progress-track {
          width: 100%;
          height: 8px;
          background: #3a3a3a;
          border-radius: 4px;
          overflow: hidden;
        }

        .tour-progress-fill {
          height: 100%;
          background: #E62F88;
          transition: width 0.3s ease;
          border-radius: 4px;
        }

        .tour-actions {
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
        }

        .tour-btn-spacer {
          grid-column: 2;
        }

        .tour-actions :global(button) {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.4rem;
          padding: 0.65rem 1rem;
          font-size: 0.9rem;
          font-weight: 600;
          min-height: 42px;
          border-radius: 8px;
        }

        .tour-actions :global(.tour-btn-skip) {
          background: transparent;
          border: 2px solid #555555;
          color: #cccccc;
        }

        .tour-actions :global(.tour-btn-skip:hover) {
          background: #333333;
          border-color: #666666;
        }

        .tour-actions :global(.tour-btn-back) {
          background: transparent;
          border: 2px solid #555555;
          color: #cccccc;
          grid-column: 2;
        }

        .tour-actions :global(.tour-btn-back:hover) {
          background: #333333;
          border-color: #666666;
        }

        .tour-actions :global(.tour-btn-next) {
          background: #E62F88;
          color: #000000;
          border: none;
          grid-column: 3;
        }

        .tour-actions :global(.tour-btn-next:hover) {
          background: #ff4099;
        }

        @media (max-width: 768px) {
          .tour-modal {
            padding: 2rem 1.5rem;
            border-radius: 16px;
          }

          .tour-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 1rem;
            margin-bottom: 1.5rem;
          }

          .tour-icon {
            width: 50px;
            height: 50px;
          }

          .tour-title {
            font-size: 2rem;
          }

          .tour-content {
            font-size: 1.1rem;
            margin-bottom: 2rem;
          }

          .tips-heading {
            font-size: 1.3rem;
          }

          .tips-list li {
            font-size: 1rem;
          }

          .tour-restart-note {
            font-size: 1rem;
          }

          .tour-step-text {
            font-size: 0.95rem;
          }

          .tour-actions {
            grid-template-columns: 1fr;
            gap: 0.75rem;
          }

          .tour-btn-spacer {
            display: none;
          }

          .tour-actions :global(.tour-btn-back) {
            grid-column: 1;
            order: 2;
          }

          .tour-actions :global(.tour-btn-next) {
            grid-column: 1;
            order: 3;
          }

          .tour-actions :global(.tour-btn-skip) {
            order: 1;
          }

          .tour-actions :global(button) {
            font-size: 1rem;
            padding: 0.75rem 1rem;
            min-height: 48px;
          }
        }
      `}</style>
    </div>
  );
};

export default ProductTour;
